import {
  Chip,
  CircularProgress,
  Autocomplete as MUIAutocomplete,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  UserAutocompleteDetailsFragment,
  useSelfQuery,
} from '../../../graphql/types';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import {
  BLACK,
  BORDER_GRAY,
  COOL_GRAY100,
  DISABLED_OPACITY,
  ICON_SECONDARY,
  SECONDARY_DISABLED,
  TEXT_DISABLED,
  WHITE,
} from '../../../theme';
import { getMemberName } from '../../../utils/getMemberName';
import { EventLabels, EventNames } from '../../../utils/trackingEvents';
import Avatar from '../Avatar';
import Errors from '../ErrorMessage';
import Text, { TextColor, TextKind, textStyleFrom } from '../Text';

const StyledAutoCompleteContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`;

const StyledMuiAutocomplete: typeof MUIAutocomplete = styled(MUIAutocomplete)`
  .MuiAutocomplete-inputRoot {
    padding: 0 40px 0 0 !important;
  }
  fieldset {
    border: none !important;
  }
`;

const TextInputFontStyles = textStyleFrom(TextKind.BodyM, TextColor.Primary);

const StyledMuiTextField = styled(TextField)`
  border-radius: 8px !important;
  border: 1px solid ${BORDER_GRAY} !important;
  background: ${WHITE};
  box-sizing: border-box;

  input {
    padding: 8px !important;

    & {
      ${{ ...TextInputFontStyles }}
    }
    &::placeholder,
    &::-webkit-input-placeholder {
      color: ${ICON_SECONDARY};
      opacity: 1;
    }
  }

  .Mui-disabled {
    cursor: default;

    color: ${TEXT_DISABLED} !important;
    background: ${COOL_GRAY100};
    -webkit-text-fill-color: ${TEXT_DISABLED} !important; // really?
  }
`;

const StyledOption = styled.li<{ $isSelected: boolean }>`
  padding: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
  background: ${({ $isSelected }) => ($isSelected ? SECONDARY_DISABLED : 'white')};
  color: ${({ $isSelected }) => ($isSelected ? TEXT_DISABLED : 'inherit')};
  cursor: ${({ $isSelected }) => ($isSelected ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ $isSelected }) => ($isSelected ? 'none' : 'auto')};
  opacity: ${({ $isSelected }) => ($isSelected ? DISABLED_OPACITY : 1)};
`;

const StyledChip = styled(Chip)`
  color: ${WHITE};
  background-color: ${BLACK};

  & > svg {
    fill: ${TEXT_DISABLED};
  }
`;

const StyledChipBox = styled(Chip)`
  color: ${BLACK};
  background-color: ${COOL_GRAY100};
  border-radius: 8px;

  & > svg {
    fill: ${BLACK};
  }
`;

const PillContainer = styled.div<{ $showAllPills: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: ${({ $showAllPills }) => ($showAllPills ? '200px' : 'auto')};
  overflow-y: ${({ $showAllPills }) => ($showAllPills ? 'auto' : 'visible')};
  padding: ${({ $showAllPills }) => ($showAllPills ? '4px 4px 0' : '0')};
  box-sizing: border-box;
`;

interface MemberBulkAutoCompleteProps {
  members: UserAutocompleteDetailsFragment[];
  onInputChange?: (value: string) => void;
  onSelection: (members: UserAutocompleteDetailsFragment[]) => void;
  onDeletePill: (deletedUserId: string) => void;
  selectedMemberIds: string[];
  loading?: boolean;
  name?: string;
  trackingData?: TrackingEventData;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  rerenderKey?: number;
  showAllPills?: boolean;
  setShowAllPills?: (show: boolean) => void;
}

export const MemberBulkAutocomplete: React.FC<MemberBulkAutoCompleteProps> = ({
  members,
  onInputChange,
  loading,
  onSelection,
  onDeletePill,
  selectedMemberIds,
  name,
  trackingData,
  label,
  placeholder,
  disabled,
  rerenderKey = 0,
  showAllPills = false,
  setShowAllPills = () => {},
}) => {
  const { track } = useTracking();
  const { data: selfData } = useSelfQuery();
  const selfId = selfData?.self?.id;

  const [inputValue, setInputValue] = useState('');

  const handleFocus = () => {
    if (trackingData) {
      track(EventNames.DropDownMenuFocus, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
  };

  const handleOpen = () => {
    if (trackingData) {
      track(EventNames.DropDownMenuClick, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
  };

  const handleSelection = (newValue: UserAutocompleteDetailsFragment[]) => {
    if (trackingData) {
      track(EventNames.DropDownMenuItemClick, {
        event_label: EventLabels.Member,
        ...trackingData,
      });
    }
    onSelection(newValue);
  };

  const handleInputChange = (
    _: React.ChangeEvent<{}>,
    newInputValue: string,
  ) => {
    setInputValue(newInputValue);
    if (onInputChange) {
      onInputChange(newInputValue);
    }
  };

  useEffect(() => {
    if (selectedMemberIds.length === 0) {
      setShowAllPills(false);
    }
  }, [selectedMemberIds, setShowAllPills]);

  return (
    <StyledAutoCompleteContainer>
      {label && (
        <Text
          component="label"
          kind={TextKind.LabelL}
          htmlFor={`memberBulkAutoComplete-${name}`}
        >
          {label}
        </Text>
      )}

      <StyledMuiAutocomplete
        multiple
        data-testid="autocomplete-search"
        id={`memberBulkAutoComplete-${name}`}
        options={loading ? [] : [...members]}
        getOptionLabel={(option) =>
          `${getMemberName(option)}${option.id === selfId ? ' (Me)' : ''}`
        }
        getOptionKey={(option) => option.id}
        isOptionEqualToValue={(option) => selectedMemberIds.includes(option.id)}
        onChange={(_, newValue) => handleSelection(newValue)}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        onOpen={() => handleOpen()}
        slotProps={{
          clearIndicator: { sx: { display: 'none' } },
        }}
        loading={loading}
        onFocus={handleFocus}
        disabled={disabled}
        filterOptions={(x) => x} // Disable built-in filtering
        renderTags={() => null} // Disable tags in the input
        renderInput={(params) => (
          <StyledMuiTextField
            {...params}
            disabled={disabled}
            placeholder={
              selectedMemberIds.length === 0
                ? (placeholder ?? `Select members`)
                : ''
            } // Remove placeholder if there are selected users
            InputProps={{
              ...params.InputProps,
              disabled,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const isSelected = selectedMemberIds.includes(option.id);
          const { key, ...otherProps } = props;
          return (
            <StyledOption
              key={key}
              $isSelected={isSelected}
              {...otherProps}
              aria-disabled={isSelected}
            >
              <Avatar user={option} size={24} />
              <Text kind={TextKind.BodyM}>
                {getMemberName(option)}
                {option.id === selfId ? ' (Me)' : ''}
              </Text>
            </StyledOption>
          );
        }}
      />

      {name && <Errors name={name} errorTextKind={TextKind.BodyS} />}

      <PillContainer
        data-testid="autocomplete-select-results"
        $showAllPills={showAllPills}
        key={`pill-container-${rerenderKey}`}
      >
        {showAllPills || selectedMemberIds.length <= 1 ? (
          members
            .filter(({ id }) => selectedMemberIds.includes(id))
            .map((user) => (
              <StyledChip
                data-testid={`autocomplete-chip-${user.id}`}
                key={user.id}
                label={getMemberName(user)}
                onDelete={() => onDeletePill(user.id)}
              />
            ))
        ) : (
          <>
            <StyledChip
              label={getMemberName(
                members.find(({ id }) => id === selectedMemberIds[0]),
              )}
              onDelete={() => onDeletePill(selectedMemberIds[0])}
            />
            <StyledChipBox
              label={`+${selectedMemberIds.length - 1} more`}
              onClick={() => setShowAllPills(true)}
            />
          </>
        )}
      </PillContainer>
    </StyledAutoCompleteContainer>
  );
};
